// Web-only helpers that mirror the mobile presets and provide
// (1) a CSS filter string for <img>/<video>
// (2) an optional tint overlay (color + opacity) for extra “mood”

// --- Preset types (kept identical-ish to mobile, just more of them) ---
export type FilterPresetId =
  | 'none'
  | 'soft'
  | 'warm'
  | 'icy'
  | 'pastel'
  | 'sunny'
  | 'frozen'
  | 'dusk'
  | 'noir'
  | 'gold'
  | 'rose'
  | 'teal'
  | 'amber'
  | 'film'
  | 'cinema';

export type FilterPreset = {
  id: FilterPresetId;
  label: string;
  hint?: string;
  thumbUri?: string;
};

// Order here = order in the sidebar.
// Top = most subtle, bottom = strongest.
export const FILTER_PRESETS: FilterPreset[] = [
  { id: 'none',     label: 'Original', hint: 'no adjustments' },

  // Very subtle looks
  { id: 'soft',     label: 'Soft',     hint: 'gentle contrast & clarity' },
  { id: 'warm', label: 'Warm', hint: 'light warm glow' },
  { id: 'icy', label: 'Icy', hint: 'light cool balance' },
  { id: 'pastel', label: 'Pastel', hint: 'slight fade & softness' },

  // Medium strength
  { id: 'sunny',  label: 'Sunny',  hint: 'orangey warmth, a bit stronger' },
  { id: 'frozen',  label: 'Frozen',  hint: 'cool modern tone' },
  { id: 'dusk',  label: 'Dusk',  hint: 'violet evening wash' },
  { id: 'noir',  label: 'Noir',  hint: 'clean B&W contrast' },
  { id: 'gold',  label: 'Gold',  hint: 'soft golden glow' },
  { id: 'rose',  label: 'Rose',  hint: 'pinkish tint' },
  { id: 'teal',  label: 'Teal',  hint: 'teal wash' },
  { id: 'amber', label: 'Amber', hint: 'warm amber haze' },
  { id: 'film',  label: 'Film',  hint: 'faded cinematic tone' },

  // Strongest at the bottom
  { id: 'cinema', label: 'Cinema', hint: 'deeper, punchier movie grade' },
];

// --- Web styles returned by helpers ---
export type OverlayStyle = { tint?: string; opacity?: number };
export type WebFilterStyle = {
  cssFilter?: string;     // value for CSS `filter:`
  overlay?: OverlayStyle; // optional tint overlay (color + opacity)
};

// Tint table – lighter opacities at the top, stronger at the bottom
const OVERLAYS: Record<FilterPresetId, OverlayStyle> = {
  none:      { tint: undefined,  opacity: 0 },

  // Subtle
  soft:      { tint: '#ffffff',  opacity: 0.02 },
  warm:  { tint: '#FFB284',  opacity: 0.04 },
  icy:  { tint: '#7FB3FF',  opacity: 0.04 },
  pastel:  { tint: '#403d39',  opacity: 0.04 },

  // Medium
  sunny:      { tint: '#FFB284',  opacity: 0.07 },  // was 0.10
  frozen:      { tint: '#7FB3FF',  opacity: 0.07 },  // was 0.10
  dusk:      { tint: '#7050A8',  opacity: 0.09 },  // was 0.12
  noir:      { tint: '#000000',  opacity: 0.10 },  // slightly softer
  gold:      { tint: '#ffd76a',  opacity: 0.08 },  // was 0.09
  rose:      { tint: '#ff6b81',  opacity: 0.08 },  // was 0.09
  teal:      { tint: '#2ac7b5',  opacity: 0.08 },  // was 0.09
  amber:     { tint: '#ffb300',  opacity: 0.09 },  // was 0.10
  film:      { tint: '#403d39',  opacity: 0.09 },  // was 0.10

  // Strongest
  cinema:    { tint: '#1f2933',  opacity: 0.12 },
};

// Lightweight, GPU-friendly CSS filters chosen to “feel like” the mobile tints.
// Top part is very subtle, bottom part a bit stronger.
const CSS_FILTERS: Record<FilterPresetId, string | undefined> = {
  none: undefined,

  // Subtle
  soft:     'contrast(1.01) saturate(1.01)',
  warm: 'saturate(1.02) contrast(1.01) sepia(0.03)',
  icy: 'saturate(1.01) hue-rotate(185deg) contrast(1.01)',
  pastel: 'contrast(0.99) saturate(0.97)',

  // Medium (toned down vs your original)
  sunny:  'saturate(1.03) contrast(1.01) sepia(0.05)',
  frozen:  'saturate(1.02) hue-rotate(185deg) contrast(1.01)',
  dusk:  'saturate(1.02) contrast(1.02) hue-rotate(250deg)',
  noir:  'grayscale(1) contrast(1.04)', // was 1.06
  gold:  'saturate(1.02) sepia(0.08) contrast(1.01)',
  rose:  'saturate(1.03) hue-rotate(325deg)',
  teal:  'saturate(1.03) hue-rotate(170deg)',
  amber: 'saturate(1.03) sepia(0.08)',
  film:  'contrast(0.985) saturate(0.94) sepia(0.05)',

  // Strongest
  cinema: 'contrast(1.06) saturate(1.08) sepia(0.10)',
};

// Main API: get everything you need to render the look in the browser.
export function getWebFilterStyle(id: FilterPresetId): WebFilterStyle {
  return {
    cssFilter: CSS_FILTERS[id],
    overlay: OVERLAYS[id],
  };
}

// Tiny convenience helpers (optional)
export function getCssFilter(id: FilterPresetId) {
  return CSS_FILTERS[id];
}

export function getOverlayForFilter(id: FilterPresetId): OverlayStyle {
  return OVERLAYS[id] ?? { tint: undefined, opacity: 0 };
}
